import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { FolderPlus, Pin, PinOff, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { LinkPicker, type LinkTarget } from "@/components/notes/LinkPicker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useGoals, usePillars } from "@/lib/plan";
import {
  NOTES_QUERY_KEY,
  PROJECT_STATUSES,
  fetchNotesData,
  noteExcerpt,
  projectStatusLabel,
  type Note,
} from "@/lib/notes";

export const Route = createFileRoute("/_authenticated/notes")({
  validateSearch: (search: Record<string, unknown>) => ({
    task: typeof search["task"] === "string" ? (search["task"] as string) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Notes & Projects — MyPriorities" },
      {
        name: "description",
        content:
          "Capture working notes, group them into projects and link them to the tasks, meetings, goals and pillars they belong to.",
      },
      { property: "og:title", content: "Notes & Projects — MyPriorities" },
      {
        property: "og:description",
        content: "Working notes linked to the tasks and meetings they came from.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: NotesPage,
});

const ALL = "all";
const NO_PROJECT = "none";

function NotesPage() {
  const queryClient = useQueryClient();
  const { task: taskFilter } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState<string>(ALL);
  const [showArchived, setShowArchived] = useState(false);
  const [newProject, setNewProject] = useState("");

  const pillars = usePillars();
  const goals = useGoals();

  const data = useQuery({ queryKey: NOTES_QUERY_KEY, queryFn: fetchNotesData });

  const tasks = useQuery({
    queryKey: ["notes_tasks"],
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("tasks")
        .select("id, title, recurrence, done")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return rows ?? [];
    },
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: ["note_link_counts"] });
  };

  const notes = data.data?.notes ?? [];
  const projects = data.data?.projects ?? [];
  const links = data.data?.links ?? [];

  const selected = notes.find((n) => n.id === selectedId) ?? null;
  const selectedLinks = links.filter((l) => l.note_id === selectedId);

  const targets: LinkTarget[] = useMemo(() => {
    const taskTargets: LinkTarget[] = (tasks.data ?? []).map((t) => ({
      id: t.id,
      kind: "task" as const,
      label: t.title,
      ...(t.recurrence && t.recurrence !== "none" ? { hint: "· regular" } : {}),
    }));
    const goalTargets: LinkTarget[] = (goals.data ?? []).map((g) => ({
      id: g.id,
      kind: "goal" as const,
      label: g.name,
    }));
    const pillarTargets: LinkTarget[] = (pillars.data ?? []).map((p) => ({
      id: p.id,
      kind: "pillar" as const,
      label: p.name,
    }));
    return [...taskTargets, ...goalTargets, ...pillarTargets];
  }, [tasks.data, goals.data, pillars.data]);

  const taskLinkedIds = useMemo(
    () =>
      new Set(
        links
          .filter((l) => l.target_kind === "task" && l.target_id === taskFilter)
          .map((l) => l.note_id),
      ),
    [links, taskFilter],
  );
  const taskFilterLabel = (tasks.data ?? []).find((t) => t.id === taskFilter)?.title ?? null;

  const visible = notes.filter((note) => {
    if (taskFilter && !taskLinkedIds.has(note.id)) return false;
    if (note.archived !== showArchived) return false;
    if (projectFilter !== ALL) {
      if (
        projectFilter === NO_PROJECT ? note.project_id !== null : note.project_id !== projectFilter
      )
        return false;
    }
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return `${note.title} ${note.body}`.toLowerCase().includes(q);
  });

  const createNote = useMutation({
    mutationFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) throw new Error("Not signed in");
      const { data: row, error } = await supabase
        .from("notes")
        .insert({
          user_id: userId,
          title: "Untitled note",
          body: "",
          ...(projectFilter !== ALL && projectFilter !== NO_PROJECT
            ? { project_id: projectFilter }
            : {}),
        })
        .select("id")
        .single();
      if (error) throw error;
      if (taskFilter) {
        await supabase
          .from("note_links")
          .insert({ user_id: userId, note_id: row.id, target_kind: "task", target_id: taskFilter });
      }
      return row.id;
    },
    onSuccess: async (id) => {
      setShowArchived(false);
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ["note_link_counts"] });
      setSelectedId(id);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateNote = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Note> }) => {
      const { error } = await supabase.from("notes").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => void refresh(),
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteNote = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("notes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      setSelectedId(null);
      await refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const createProject = useMutation({
    mutationFn: async (name: string) => {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) throw new Error("Not signed in");
      const { error } = await supabase.from("projects").insert({ user_id: userId, name });
      if (error) throw error;
    },
    onSuccess: async () => {
      setNewProject("");
      await refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const setProjectStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("projects").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => void refresh(),
    onError: (error: Error) => toast.error(error.message),
  });

  const toggleLink = useMutation({
    mutationFn: async ({
      noteId,
      target,
      linked,
    }: {
      noteId: string;
      target: LinkTarget;
      linked: boolean;
    }) => {
      if (linked) {
        const { error } = await supabase
          .from("note_links")
          .delete()
          .eq("note_id", noteId)
          .eq("target_kind", target.kind)
          .eq("target_id", target.id);
        if (error) throw error;
        return;
      }
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) throw new Error("Not signed in");
      const { error } = await supabase.from("note_links").insert({
        user_id: userId,
        note_id: noteId,
        target_kind: target.kind,
        target_id: target.id,
      });
      if (error) throw error;
    },
    onSuccess: () => void refresh(),
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <AppShell
      title="Notes & Projects"
      subtitle="Working notes, grouped into projects and linked to the work they belong to."
      action={
        <Button size="sm" onClick={() => createNote.mutate()} disabled={createNote.isPending}>
          <Plus className="size-4" /> New note
        </Button>
      }
    >
      <div className="grid fill-track gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <div className="space-y-4">
          <div className="space-y-3 rounded-xl border border-border bg-card p-3">
            {taskFilter ? (
              <div className="flex items-center gap-2 rounded-md border border-border bg-surface px-2 py-1.5 text-xs">
                <span className="flex-1 truncate">Notes for {taskFilterLabel ?? "this task"}</span>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => void navigate({ search: { task: undefined } })}
                >
                  Clear
                </button>
              </div>
            ) : null}
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search notes"
                className="pl-8"
              />
            </div>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All projects</SelectItem>
                <SelectItem value={NO_PROJECT}>No project</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-xs"
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? "Showing archived — show active" : "Show archived"}
            </Button>
          </div>

          <div className="space-y-2">
            {data.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading notes…</p>
            ) : visible.length === 0 ? (
              <p className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
                No notes here yet. Create one to start capturing working notes.
              </p>
            ) : (
              visible.map((note) => {
                const project = projects.find((p) => p.id === note.project_id);
                const count = links.filter((l) => l.note_id === note.id).length;
                return (
                  <button
                    key={note.id}
                    type="button"
                    onClick={() => setSelectedId(note.id)}
                    className={`w-full rounded-lg border p-3 text-left transition-colors ${
                      note.id === selectedId
                        ? "border-primary bg-surface"
                        : "border-border bg-card hover:bg-surface"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {note.pinned ? <Pin className="size-3 text-primary" /> : null}
                      <span className="flex-1 truncate text-sm font-medium">{note.title}</span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                      {noteExcerpt(note.body) || "Empty note"}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {project ? (
                        <Badge variant="outline" className="text-[0.7rem]">
                          {project.name}
                        </Badge>
                      ) : null}
                      {count > 0 ? (
                        <Badge variant="secondary" className="text-[0.7rem]">
                          {count} link{count === 1 ? "" : "s"}
                        </Badge>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="space-y-3 rounded-xl border border-border bg-card p-3">
            <p className="text-sm font-medium">Projects</p>
            <div className="flex gap-2">
              <Input
                value={newProject}
                onChange={(e) => setNewProject(e.target.value)}
                placeholder="New project"
              />
              <Button
                size="icon"
                variant="outline"
                disabled={!newProject.trim() || createProject.isPending}
                onClick={() => createProject.mutate(newProject.trim())}
                aria-label="Add project"
              >
                <FolderPlus className="size-4" />
              </Button>
            </div>
            {projects.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Projects group related notes together.
              </p>
            ) : (
              <ul className="space-y-2">
                {projects.map((p) => (
                  <li key={p.id} className="flex items-center gap-2">
                    <span className="flex-1 truncate text-sm">{p.name}</span>
                    <Select
                      value={p.status}
                      onValueChange={(status) => setProjectStatus.mutate({ id: p.id, status })}
                    >
                      <SelectTrigger className="h-8 w-28 text-xs">
                        <SelectValue placeholder={projectStatusLabel(p.status)} />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {selected ? (
          <div className="space-y-4 rounded-xl border border-border bg-card p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={selected.title}
                onChange={(e) =>
                  queryClient.setQueryData(NOTES_QUERY_KEY, (old: typeof data.data) =>
                    old
                      ? {
                          ...old,
                          notes: old.notes.map((n) =>
                            n.id === selected.id ? { ...n, title: e.target.value } : n,
                          ),
                        }
                      : old,
                  )
                }
                onBlur={(e) =>
                  updateNote.mutate({ id: selected.id, patch: { title: e.target.value } })
                }
                className="flex-1 font-display text-base font-semibold"
              />
              <Button
                variant="outline"
                size="icon"
                aria-label={selected.pinned ? "Unpin note" : "Pin note"}
                onClick={() =>
                  updateNote.mutate({ id: selected.id, patch: { pinned: !selected.pinned } })
                }
              >
                {selected.pinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  updateNote.mutate({ id: selected.id, patch: { archived: !selected.archived } })
                }
              >
                {selected.archived ? "Restore" : "Archive"}
              </Button>
              <Button
                variant="outline"
                size="icon"
                aria-label="Delete note"
                onClick={() => deleteNote.mutate(selected.id)}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="note-project">Project</Label>
                <Select
                  value={selected.project_id ?? NO_PROJECT}
                  onValueChange={(v) =>
                    updateNote.mutate({
                      id: selected.id,
                      patch: { project_id: v === NO_PROJECT ? null : v },
                    })
                  }
                >
                  <SelectTrigger id="note-project">
                    <SelectValue placeholder="No project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_PROJECT}>No project</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Linked to</Label>
                <LinkPicker
                  targets={targets}
                  links={selectedLinks}
                  onToggle={(target, linked) =>
                    toggleLink.mutate({ noteId: selected.id, target, linked })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="note-body">Note</Label>
              <Textarea
                id="note-body"
                value={selected.body}
                rows={16}
                placeholder="What happened, what matters, what's next…"
                onChange={(e) =>
                  queryClient.setQueryData(NOTES_QUERY_KEY, (old: typeof data.data) =>
                    old
                      ? {
                          ...old,
                          notes: old.notes.map((n) =>
                            n.id === selected.id ? { ...n, body: e.target.value } : n,
                          ),
                        }
                      : old,
                  )
                }
                onBlur={(e) =>
                  updateNote.mutate({ id: selected.id, patch: { body: e.target.value } })
                }
              />
              <p className="text-xs text-muted-foreground">Saves when you click away.</p>
            </div>
          </div>
        ) : (
          <div className="flex min-h-[20rem] items-center justify-center rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Pick a note on the left, or create a new one.
          </div>
        )}
      </div>
    </AppShell>
  );
}
